import React, { FC, forwardRef, useRef, useState } from "react";
import { AxiosResponse } from "axios";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { ReactComponent as LoadingSVG } from "go-core/images/svg/loading.svg";
import { ReportApi } from "go-report/core/services/types";
import { DashboardFetchActions } from "../../../services/data/reducer";
import useDashboardApi from "../../../services/hooks/useDashboardApi";
import useDashboardContext from "../../../services/hooks/useDashboardContext";
import { ChartDataRangeType, ChartType, DashboardCardConfig } from "../../../services/types";
import { getDashboardChartActionType } from "../../../utils/typeUtils";
import ReportItemWrapperFooter from "./ReportItemWrapperFooter";
import ReportItemWrapperHeader from "./ReportItemWrapperHeader";

interface Props {
	config: DashboardCardConfig;
}

const ReportItemWrapper: FC<Props> = forwardRef<HTMLDivElement, Props>(({ config, children }, ref) => {
	const { type, chartDataRangeType, chartType, cursorType, chartConfigName } = config;
	const {
		dispatch,
		isEditMode,
		state: { chartsConfig },
	} = useDashboardContext();
	const { addFlash } = useFlash();
	const [loading, setLoading] = useState(false);
	const reloadReportItemAbortController = useRef<AbortController | undefined>(undefined);
	const {
		fetchSalesReports,
		fetchCategoryReports,
		fetchProductReports,
		fetchPaymentReports,
		fetchSalesChartReports,
	} = useDashboardApi();

	const reloadReportData = async (
		func: (controller: AbortController, rangeType?: ChartDataRangeType) => Promise<AxiosResponse<ReportApi>>,
		rangeType: ChartDataRangeType
	) => {
		setLoading(true);

		try {
			if (reloadReportItemAbortController.current) {
				reloadReportItemAbortController.current.abort();
			}
			reloadReportItemAbortController.current = new AbortController();

			const response = await func(reloadReportItemAbortController.current, rangeType);
			dispatch({
				type: DashboardFetchActions[`FETCH_${type}S`],
				data: response.data,
			});
		} catch (e) {
			handleError.alert(e, addFlash);
		} finally {
			setLoading(false);
		}
	};

	const updateChartRangeType = (rangeType: ChartDataRangeType) => {
		if (chartsConfig[chartConfigName].range === rangeType) return;

		dispatch({
			type: getDashboardChartActionType(type),
			data: {
				range: rangeType,
				type: chartType,
			},
		});

		reloadReport(rangeType);
	};

	const updateChartType = (newChartType: ChartType) => {
		dispatch({
			type: getDashboardChartActionType(type),
			data: {
				range: chartDataRangeType,
				type: newChartType,
			},
		});
	};

	const reloadReport = (rangeType: ChartDataRangeType) => {
		switch (type) {
			case "PRODUCT_REPORT":
				reloadReportData(fetchProductReports, rangeType);
				break;
			case "CATEGORY_REPORT":
				reloadReportData(fetchCategoryReports, rangeType);
				break;
			case "PAYMENT_REPORT":
				reloadReportData(fetchPaymentReports, rangeType);
				break;
			case "SALES_REPORT":
				reloadReportData(fetchSalesReports, rangeType);
				break;
			case "SALES_ADVANCED_REPORT":
				reloadReportData(fetchSalesChartReports, rangeType);
				break;
			default:
				break;
		}
	};
	const cursorTypeClassname = isEditMode ? `is-dashboard-box-${cursorType}` : "";

	return (
		<div ref={ref} className={`report-item-wrapper ${cursorTypeClassname}`}>
			<ReportItemWrapperHeader config={config} updateChartType={updateChartType} />
			{children}
			{loading && <LoadingSVG className={"loading-custom"} />}
			<ReportItemWrapperFooter updateChartRangeType={updateChartRangeType} config={config} />
		</div>
	);
});

ReportItemWrapper.displayName = "ReportItemWrapper";

export default ReportItemWrapper;
